<script setup lang='ts'>
import OperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue';
import ManagementOfficeAutoComplete from '@/components/AutoCompletes/ManagementOfficeAutoComplete.vue';
import DepartmentAutoComplete from '@/components/AutoCompletes/DepartmentAutoComplete.vue';
import VehicleAutoComplete from '@/components/AutoCompletes/VehicleAutoComplete.vue';
import DriverAutoComplete from '@/components/AutoCompletes/DriverAutoComplete.vue';
import GroupAutoComplete from '@/components/AutoCompletes/GroupAutoComplete.vue';
import managementOfficeType from '@/types/responses/getManagementOffice';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import operationOfficeType from '@/types/responses/getOperationOffice';
import RangeDatePicker from '@/components/Common/RangeDatePicker.vue';
import departmentResponseType from '@/types/responses/getDepartment';
import vehicleResponseType from '@/types/responses/getVehicle';
import accountResponseType from '@/types/responses/getAccount';
import groupResponseType from '@/types/responses/getGroup';
import { computed } from 'vue';
import i18n from '@/lang/i18n';

const emit = defineEmits(['update:modelValue']);
const props = defineProps<{
  modelValue: {
    managementOffice: managementOfficeType | undefined
    operationOffice: operationOfficeType | undefined
    group: groupResponseType | undefined
    department: departmentResponseType | undefined
    vehicle: vehicleResponseType | undefined
    driver: accountResponseType | undefined
    selectDateType: string
    dateRange: Date[]
  }
  operationOffices: any
  managementOfficeClearable: boolean
  operationOfficeClearable: boolean
  isDepartment: boolean
  errors: { [key: string]: string[] }
  maxFilterDays: number
}>()

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
})
</script>
<template>
  <div class="d-grid gap-2">
    <ManagementOfficeAutoComplete
      v-model="modelValue.managementOffice"
      :error-messages="props.errors.managementOffice"
      :selectFirstIfOnlyOne=true
      :clearable="props.managementOfficeClearable"
    />
    <OperationOfficeAutoComplete
      v-model="modelValue.operationOffice"
      :managementOffice="modelValue.managementOffice"
      :error-messages="props.errors.operationOffice"
      :selectFirstIfOnlyOne=true
      :clearable="props.operationOfficeClearable"
    />
    <GroupAutoComplete
      v-model="modelValue.group"
      :managementOffice="modelValue.managementOffice"
      :operationOffices="props.operationOffices"
      :error-messages="props.errors.group"
    />
    <DepartmentAutoComplete
      v-model="modelValue.department"
      :operationOffices="props.operationOffices"
      :error-messages="props.errors.department"
      :selectFirstIfOnlyOne="props.isDepartment"
    />
    <VehicleAutoComplete
      v-model="modelValue.vehicle"
      :selectedDepartment="modelValue.department"
      :managementOffice="modelValue.managementOffice"
      :operationOffices="props.operationOffices"
      :requestParams="{ type: 'filter' }"
      :error-messages="props.errors.vehicle"
    />
    <DriverAutoComplete
      v-model="modelValue.driver"
      :selectedDepartment="modelValue.department"
      :operationOffices="props.operationOffices"
      :error-messages="props.errors.driver"
    />
    <div class="p-1">
      <v-radio-group v-model="modelValue.selectDateType" class="radio-group-row">
        <v-radio
          value="leaving"
          :label="i18n.global.t('pages.common.filter.leaving')"
          color="blue"
        />
        <v-radio
          value="returning"
          :label="i18n.global.t('pages.common.filter.returning')"
          color="blue"
        />
      </v-radio-group>
    </div>
    <validation-errors :messages="props.errors.end_date">
      <div style="font-size: 12px" class="d-flex justify-space-between m-1">
        <RangeDatePicker
          v-model="modelValue.dateRange"
          :maxRange="props.maxFilterDays"
          :format="'yyyy/MM/dd'"
          :isError="errors.end_date?.length > 0"
        />
      </div>
    </validation-errors>
  </div>
</template>
